import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('error');
          return;
        }
        attempts++;
        setTimeout(poll, pollInterval);
      } catch (e) {
        console.error('Error polling status', e);
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <div data-testid="payment-loading">
            <Loader2 size={48} className="animate-spin text-[#FF7043] mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Verifica pagamento...</h2>
            <p className="text-slate-500">Stiamo verificando il tuo pagamento</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={40} className="text-green-600" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Pagamento Completato!
            </h2>
            <p className="text-slate-500 mb-8">
              Grazie per il tuo acquisto! Riceverai una conferma via email.
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="success-go-home"
              className="px-8 py-3 bg-[#FF7043] text-white font-heading font-bold rounded-full btn-caribbean"
            >
              Torna alla Home
            </button>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="payment-error">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-red-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">
              Pagamento non riuscito
            </h2>
            <p className="text-slate-500 mb-8">
              Si è verificato un errore. Riprova o contattaci.
            </p>
            <button
              onClick={() => navigate('/shop')}
              data-testid="error-go-shop"
              className="px-8 py-3 bg-[#0288D1] text-white font-heading font-bold rounded-full btn-caribbean btn-blue"
            >
              Torna allo Shop
            </button>
          </div>
        )}

        {status === 'timeout' && (
          <div data-testid="payment-timeout">
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 size={40} className="text-yellow-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">
              Verifica in corso
            </h2>
            <p className="text-slate-500 mb-8">
              Non siamo riusciti a verificare il pagamento. Riceverai una conferma via email.
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="timeout-go-home"
              className="px-8 py-3 bg-[#FF7043] text-white font-heading font-bold rounded-full btn-caribbean"
            >
              Torna alla Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
